import { Injectable } from '@angular/core';
import { HubConnection } from '@microsoft/signalr';
import { statsAndResourcesCacheBuster$ } from '@shared/constants/cache-busters';
import { hubEvent } from '@shared/constants/hub-event';
import { IDashboardGroup } from '@shared/interfaces/dashboard/dashboard-group';
import { IGroupPublished } from '@shared/interfaces/group/group-published';
import { IStatsAndResources } from '@shared/interfaces/resources/stats-and-resources';
import { map, Observable, Subject } from 'rxjs';
import { SignalRService } from '../signal-r.service';

@Injectable({
  providedIn: 'root'
})
export class UserPublisherService {
  private newUserDataPublished$ = new Subject<void>();
  private statsAndResourcesPublished$ = new Subject<IStatsAndResources>();
  private groupPublished$ = new Subject<IGroupPublished>();
  private fileUploadingProgressPublished$ = new Subject<number>();

  constructor(
    private readonly signalRService: SignalRService
  ) {
    this.signalRService.hubConnection$.subscribe(hubConnection => {
      if (hubConnection) this.subscribeEvents(hubConnection);
    });
  }

  private subscribeEvents(hubConnection: HubConnection): void {
    hubConnection.on(hubEvent.refreshUserProfile, () => {
      this.newUserDataPublished$.next();
    });

    hubConnection.on(hubEvent.refreshStatsAndResources, (statsAndResources: IStatsAndResources) => {
      statsAndResourcesCacheBuster$.next();
      this.statsAndResourcesPublished$.next(statsAndResources);
    });

    hubConnection.on(hubEvent.addGroup, (groupPublished) => {
      this.groupPublished$.next(groupPublished);
    })

    hubConnection.on(hubEvent.refreshFileUploadingProgress, (progress) => {
      this.fileUploadingProgressPublished$.next(progress);
    })
  }

  getRefreshUserDataObservable(): Observable<void> {
    return this.newUserDataPublished$.asObservable();
  }

  getStatsAndResourcesObservable(): Observable<IStatsAndResources> {
    return this.statsAndResourcesPublished$.asObservable();
  }

  getDashboardGroupObservable(): Observable<IDashboardGroup> {
    return this.groupPublished$.pipe(map(gp => {
      return {
        id: gp.id,
        name: gp.name,
        devices: []
      } as IDashboardGroup
    }))
  }

  getFileUploadingProgressObservable(): Observable<number> {
    return this.fileUploadingProgressPublished$.asObservable();
  }
}
